import React, { useState } from 'react';
import { Bell, Check, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

/**
 * QuoteRequestChecklist — a header notification bell for a quote request.
 * Icon only; a small badge shows how many items still need adding. Click to
 * open a popover whose incomplete rows jump straight to the relevant action.
 * Doubles as the general notification area for the quote request.
 *
 * @param {Array} props.items  [{ key, label, done, action }]
 */
const QuoteRequestChecklist = ({ items = [] }) => {
  const [open, setOpen] = useState(false);
  const outstandingCount = items.filter((item) => !item.done).length;

  const handleAction = (item) => {
    setOpen(false);
    item.action?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={outstandingCount > 0 ? `${outstandingCount} item${outstandingCount === 1 ? '' : 's'} to complete` : 'Notifications'}
          className="relative flex items-center justify-center h-9 w-9 rounded-lg text-foreground hover:bg-accent transition-colors"
        >
          <Bell className="h-5 w-5" />
          {outstandingCount > 0 && (
            <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
              {outstandingCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-72 p-1.5">
        {items.length === 0 ? (
          <p className="px-2.5 py-6 text-sm text-muted-foreground text-center">Nothing needs attention</p>
        ) : (
          items.map((item) => (
            item.done ? (
              <div key={item.key} className="flex items-center gap-3 px-2.5 py-2 rounded-md text-sm text-muted-foreground">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="line-through decoration-muted-foreground/40">{item.label}</span>
              </div>
            ) : (
              <button
                key={item.key}
                type="button"
                onClick={() => handleAction(item)}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-sm text-left hover:bg-accent transition-colors group"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-muted-foreground/30 group-hover:border-primary shrink-0" />
                <span className="flex-1 min-w-0 font-medium">{item.label}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
              </button>
            )
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

export default QuoteRequestChecklist;
